import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import Header from "./Header";
import Swal from "sweetalert2";
import Sidebar from "./Sidebar";
import SubHeader from "./SubHeader";
import "../Style/Dashboard.css";
import "../Style/FinancialTables.css";
import { BASE_URL } from "../Apis/apiConfig";
import {
  FileText,
  Calendar,
  Home,
  RefreshCw,
  ListOrdered,
  Search,
  Download,
  Eye,
  Filter,
  ChevronDown,
  ChevronUp,
  X,
  Image as ImageIcon,
  File,
  FileType,
  FileArchive,
  FileSpreadsheet,
  FileVideo,
  FileAudio,
  Trash2,
  Share2,
  MoreVertical,
  Upload,
  Check,
  AlertCircle,
  Loader2,
  Printer,
  ExternalLink,
  Copy,
  Theater,
} from "lucide-react";

const DocumentsList = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [documents, setDocuments] = useState([]);
  const [filteredDocuments, setFilteredDocuments] = useState([]);
  const [previewImage, setPreviewImage] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [previewPdf, setPreviewPdf] = useState(null);
  const [activeFilters, setActiveFilters] = useState({
    fileType: "all",
    dateRange: "all",
    unitNo: "",
  });
  const [selectedDocuments, setSelectedDocuments] = useState([]);
  const [sortConfig, setSortConfig] = useState({
    key: "createDate",
    direction: "desc",
  });
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadUnitNo, setUploadUnitNo] = useState("");
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef(null);

  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return "";

    try {
      const parts = dateString.split(" ");
      if (parts.length < 6) return dateString;

      const day = parts[2];
      const month = parts[1];
      const year = parts[5];
      const time = parts[3];

      const date = new Date(`${day} ${month} ${year} ${time}`);
      return date.toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch (error) {
      return dateString;
    }
  };

  const getFileIcon = (fileType) => {
    if (fileType.startsWith("image/")) return <ImageIcon size={16} />;
    if (fileType.includes("pdf")) return <FileText size={16} />;
    if (fileType.includes("word")) return <File size={16} />;
    if (fileType.includes("excel") || fileType.includes("spreadsheet"))
      return <FileSpreadsheet size={16} />;
    if (fileType.includes("zip") || fileType.includes("compressed"))
      return <FileArchive size={16} />;
    if (fileType.includes("video")) return <FileVideo size={16} />;
    if (fileType.includes("audio")) return <FileAudio size={16} />;
    return <FileType size={16} />;
  };

  const getFileColor = (fileType) => {
    if (fileType.startsWith("image/")) return "#10b981";
    if (fileType.includes("pdf")) return "#ef4444";
    if (fileType.includes("word")) return "#3b82f6";
    if (fileType.includes("excel") || fileType.includes("spreadsheet"))
      return "#22c55e";
    if (fileType.includes("zip") || fileType.includes("compressed"))
      return "#f59e0b";
    return "#6b7280";
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return "0 KB";
    if (bytes < 1024) return bytes + " B";
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " KB";
    return (bytes / (1024 * 1024)).toFixed(1) + " MB";
  };

  const fetchDocuments = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("token");
      const society = localStorage.getItem("society");
      if (!token || !society) {
        setError("Authentication required");
        return;
      }

      const resp = await axios.get(
        `${BASE_URL}/documents/get-documents-by-society-name?societyName=${encodeURIComponent(society)}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const docs = resp.data || [];
      setDocuments(docs);
      setFilteredDocuments(docs);
      console.log("Documents data:", docs);
    } catch (err) {
      console.error("Fetch documents error:", err);
      setError("Failed to load documents");
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to load documents. Please try again.",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDocuments();
  }, []);

  useEffect(() => {
    let result = [...documents];

    // Apply search filter
    if (searchQuery) {
      result = result.filter(
        (doc) =>
          doc.unitNo?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          doc.fileName?.toLowerCase().includes(searchQuery.toLowerCase()),
      );
    }

    // Apply file type filter
    if (activeFilters.fileType !== "all") {
      result = result.filter((doc) => {
        if (activeFilters.fileType === "image")
          return doc.fileType?.startsWith("image/");
        if (activeFilters.fileType === "pdf")
          return doc.fileType?.includes("pdf");
        if (activeFilters.fileType === "document")
          return (
            doc.fileType?.includes("word") || doc.fileType?.includes("document")
          );
        if (activeFilters.fileType === "spreadsheet")
          return (
            doc.fileType?.includes("excel") ||
            doc.fileType?.includes("spreadsheet")
          );
        return true;
      });
    }

    // Apply unit number filter
    if (activeFilters.unitNo) {
      result = result.filter((doc) =>
        doc.unitNo?.toLowerCase().includes(activeFilters.unitNo.toLowerCase()),
      );
    }

    // Apply sorting
    result.sort((a, b) => {
      if (sortConfig.key === "createDate") {
        const dateA = new Date(a.createDate);
        const dateB = new Date(b.createDate);
        return sortConfig.direction === "asc" ? dateA - dateB : dateB - dateA;
      }
      if (sortConfig.key === "unitNo") {
        return sortConfig.direction === "asc"
          ? a.unitNo?.localeCompare(b.unitNo)
          : b.unitNo?.localeCompare(a.unitNo);
      }
      if (sortConfig.key === "fileName") {
        return sortConfig.direction === "asc"
          ? a.fileName?.localeCompare(b.fileName)
          : b.fileName?.localeCompare(a.fileName);
      }
      return 0;
    });

    setFilteredDocuments(result);
  }, [documents, searchQuery, activeFilters, sortConfig]);

  const handleSort = (key) => {
    setSortConfig((prevConfig) => ({
      key,
      direction:
        prevConfig.key === key && prevConfig.direction === "asc"
          ? "desc"
          : "asc",
    }));
  };

  const handleDocumentSelect = (docId) => {
    setSelectedDocuments((prev) =>
      prev.includes(docId)
        ? prev.filter((id) => id !== docId)
        : [...prev, docId],
    );
  };

  const handleSelectAll = () => {
    if (selectedDocuments.length === filteredDocuments.length) {
      setSelectedDocuments([]);
    } else {
      setSelectedDocuments(filteredDocuments.map((doc) => doc.documentId));
    }
  };

  const handleDownload = (document, openInNewTab = false) => {
    try {
      const link = document.createElement("a");
      link.href = `data:${document.fileType};base64,${document.base64}`;
      link.download = document.fileName;

      if (openInNewTab) {
        link.target = "_blank";
        link.rel = "noopener noreferrer";
      }

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Sorry For Inconvinence",
        text: "Download Feature is Comming Soon.",
      });
    }
  };

  const handleShare = async (document) => {
    try {
      if (navigator.share) {
        await navigator.share({
          title: document.fileName,
          text: `Document from Unit ${document.unitNo}`,
          url: `data:${document.fileType};base64,${document.base64}`,
        });
      } else {
        navigator.clipboard.writeText(
          `Document: ${document.fileName} - Unit ${document.unitNo}`,
        );
        Swal.fire({
          icon: "success",
          title: "Copied!",
          text: "Document information copied to clipboard.",
          timer: 2000,
        });
      }
    } catch (error) {
      console.error("Share failed:", error);
    }
  };

  const handleDelete = async (docId) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#ef4444",
      cancelButtonColor: "#6b7280",
      confirmButtonText: "Yes, delete it!",
    });

    if (result.isConfirmed) {
      try {
        const token = localStorage.getItem("token");
        await axios.delete(`${BASE_URL}/documents/${docId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        setDocuments((prev) => prev.filter((doc) => doc.documentId !== docId));
        Swal.fire("Deleted!", "Document has been deleted.", "success");
      } catch (error) {
        Swal.fire("Error!", "Failed to delete document.", "error");
      }
    }
  };

  const handleUpload = async () => {
    if (!uploadFile || !uploadUnitNo.trim()) {
      Swal.fire({
        icon: "warning",
        title: "Missing Information",
        text: "Please select a file and enter unit number.",
      });
      return;
    }

    setIsUploading(true);
    try {
      const token = localStorage.getItem("token");
      const society = localStorage.getItem("society");

      const formData = new FormData();
      formData.append("file", uploadFile);
      formData.append("unitNo", uploadUnitNo);
      formData.append("societyName", society);

      await axios.post(`${BASE_URL}/documents/upload`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
      });

      Swal.fire({
        icon: "success",
        title: "Uploaded!",
        text: "Document uploaded successfully.",
        timer: 2000,
      });

      setUploadFile(null);
      setUploadUnitNo("");
      setIsUploadModalOpen(false);
      fetchDocuments();
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Upload Failed",
        text: "Failed to upload document. Please try again.",
      });
    } finally {
      setIsUploading(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const clearFilters = () => {
    setSearchQuery("");
    setActiveFilters({
      fileType: "all",
      dateRange: "all",
      unitNo: "",
    });
    setSelectedDocuments([]);
  };

  return (
    <div className="app-container">
      <style>
        {`
        .documents-container {
          width: 1200px;
          margin: 0 auto;
          padding: 24px;
        }

        .documents-card {
          background: white;
          border-radius: 16px;
          border: 1px solid #e2e8f0;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.03);
          overflow: hidden;
        }

        .documents-header {
          padding: 24px;
          border-bottom: 1px solid #e2e8f0;
          background: #f8fafc;
          display: flex;
          justify-content: space-between;
          align-items: center;
          flex-wrap: wrap;
          gap: 16px;
        }

        .documents-title {
          display: flex;
          align-items: center;
          gap: 12px;
        }

        .documents-title h2 {
          font-size: 24px;
          font-weight: 700;
          color: #1e293b;
          margin: 0;
        }

        .header-actions {
          display: flex;
          gap: 12px;
          flex-wrap: wrap;
        }

        .search-container {
          position: relative;
          width: 300px;
        }

        .search-input {
          width: 100%;
          padding: 10px 16px 10px 40px;
          border: 1px solid #e2e8f0;
          border-radius: 8px;
          font-size: 14px;
          background: white;
          transition: all 0.2s;
        }

        .search-input:focus {
          outline: none;
          border-color: #0d6efd;
          box-shadow: 0 0 0 3px rgba(13, 110, 253, 0.1);
        }

        .search-icon {
          position: absolute;
          left: 12px;
          top: 50%;
          transform: translateY(-50%);
          color: #94a3b8;
        }

        .btn {
          padding: 10px 20px;
          border-radius: 8px;
          font-size: 14px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s;
          display: flex;
          align-items: center;
          gap: 8px;
          border: none;
        }

        .btn-primary {
          background: linear-gradient(135deg, #0d6efd 0%, #0b5ed7 100%);
          color: white;
        }

        .btn-primary:hover {
          opacity: 0.9;
          transform: translateY(-1px);
        }

        .btn-secondary {
          background: white;
          border: 1px solid #e2e8f0;
          color: #475569;
        }

        .btn-secondary:hover {
          background: #f8fafc;
          border-color: #cbd5e0;
        }

        .btn-danger {
          background: #ef4444;
          color: white;
          border: none;
        }

        .btn-danger:hover {
          background: #dc2626;
        }

        .btn-success {
          background: #10b981;
          color: white;
          border: none;
        }

        .btn-success:hover {
          background: #059669;
        }

        .filters-section {
          padding: 10px 24px;
          border-bottom: 1px solid #e2e8f0;
          background: #f8fafc;
        }

        .filters-toggle {
          display: flex;
          justify-content: space-between;
          align-items: center;
          cursor: pointer;
          padding: 12px 0;
        }

        .filters-content {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 20px;
          padding-top: 16px;
          animation: slideDown 0.3s ease-out;
        }

        @keyframes slideDown {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .filter-group {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .filter-group label {
          font-size: 12px;
          font-weight: 600;
          color: #475569;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .filter-select, .filter-input {
          padding: 8px 12px;
          border: 1px solid #e2e8f0;
          border-radius: 6px;
          font-size: 14px;
          background: white;
          transition: all 0.2s;
        }

        .filter-select:focus, .filter-input:focus {
          outline: none;
          border-color: #0d6efd;
          box-shadow: 0 0 0 3px rgba(13, 110, 253, 0.1);
        }

        .clear-filters-btn {
          padding: 8px 16px;
          background: #f1f5f9;
          border: 1px solid #e2e8f0;
          border-radius: 6px;
          color: #475569;
          font-size: 14px;
          cursor: pointer;
          transition: all 0.2s;
          margin-top: 20px;
        }

        .clear-filters-btn:hover {
          background: #e2e8f0;
        }

        .documents-body {
          overflow-x: auto;
        }

        .documents-table {
          width: 100%;
          border-collapse: collapse;
        }

        .documents-table thead {
          background: #f1f5f9;
        }

        .documents-table th {
          padding: 16px;
          text-align: left;
          font-size: 12px;
          font-weight: 600;
          color: #475569;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          border-bottom: 2px solid #e2e8f0;
          cursor: pointer;
          user-select: none;
          transition: background 0.2s;
        }

        .documents-table th:hover {
          background: #e2e8f0;
        }

        .documents-table th.sorted {
          color: #0d6efd;
        }

        .sort-indicator {
          margin-left: 4px;
          opacity: 0.6;
        }

        .documents-table td {
          padding: 16px;
          
          vertical-align: middle;
        }

        .documents-table tbody tr {
          transition: background 0.2s;
        }

        .documents-table tbody tr:hover {
          background: #f8fafc;
        }

        .documents-table tbody tr.selected {
          background: #dbeafe;
        }

        .select-cell {
          width: 40px;
          text-align: center;
        }

        .file-cell {
          display: flex;
          align-items: center;
          gap: 12px;
        }

        .file-icon {
          width: 36px;
          height: 36px;
          border-radius: 8px;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .file-info {
          display: flex;
          flex-direction: column;
          gap: 4px;
        }

        .file-name {
          font-weight: 500;
          color: #1e293b;
          max-width: 250px;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .file-meta {
          display: flex;
          gap: 12px;
          font-size: 12px;
          color: #64748b;
        }

        .image-preview {
          width: 60px;
          height: 60px;
          border-radius: 8px;
          object-fit: cover;
          cursor: pointer;
          transition: transform 0.2s;
          border: 1px solid #e2e8f0;
        }

        .image-preview:hover {
          transform: scale(1.05);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
        }

        .action-buttons {
          display: flex;
          gap: 8px;
        }

        .action-btn {
          
          border-radius: 6px;
          border: 1px solid #e2e8f0;
          background: white;
          color: #64748b;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.2s;
        }

        .action-btn:hover {
          background: #f8fafc;
          border-color: #cbd5e0;
        }

        .action-btn.download:hover {
          background: #dcfce7;
          border-color: #86efac;
          color: #166534;
        }

        .action-btn.view:hover {
          background: #dbeafe;
          border-color: #93c5fd;
          color: #1d4ed8;
        }

        .action-btn.share:hover {
          background: #f3e8ff;
          border-color: #d8b4fe;
          color: #7c3aed;
        }

        .action-btn.delete:hover {
          background: #fee2e2;
          border-color: #fca5a5;
          color: #dc2626;
        }

        .empty-state {
          padding: 60px 24px;
          text-align: center;
          color: #64748b;
        }

        .empty-state h3 {
          font-size: 18px;
          font-weight: 600;
          margin: 16px 0 8px;
          color: #475569;
        }

        .loading-state {
          padding: 40px 24px;
          text-align: center;
          color: #64748b;
        }

        .loading-spinner {
          animation: spin 1s linear infinite;
        }

        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }

        .bulk-actions {
          padding: 16px 24px;
          border-top: 1px solid #e2e8f0;
          background: #f8fafc;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .selected-count {
          font-size: 14px;
          color: #475569;
          font-weight: 500;
        }

        .bulk-action-buttons {
          display: flex;
          gap: 8px;
        }

        /* Modal Styles */
        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0, 0, 0, 0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
          padding: 20px;
        }

        .modal-content {
          background: white;
          border-radius: 16px;
          padding: 32px;
          max-width: 500px;
          width: 100%;
          max-height: 90vh;
          overflow-y: auto;
          position: relative;
        }

        .modal-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 24px;
        }

        .modal-header h3 {
          font-size: 20px;
          font-weight: 600;
          color: #1e293b;
          margin: 0;
        }

        .close-modal {
          width: 32px;
          height: 32px;
          border-radius: 8px;
          border: 1px solid #e2e8f0;
          background: white;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.2s;
        }

        .close-modal:hover {
          background: #f8fafc;
          border-color: #cbd5e0;
        }

        .form-group {
          margin-bottom: 20px;
        }

        .form-group label {
          display: block;
          font-size: 14px;
          font-weight: 500;
          color: #475569;
          margin-bottom: 8px;
        }

        .file-upload-area {
          border: 2px dashed #e2e8f0;
          border-radius: 8px;
          padding: 32px;
          text-align: center;
          cursor: pointer;
          transition: all 0.2s;
        }

        .file-upload-area:hover {
          border-color: #0d6efd;
          background: #f8fafc;
        }

        .file-upload-area.has-file {
          border-color: #10b981;
          background: #f0fdf4;
        }

        .file-preview {
          display: flex;
          align-items: center;
          gap: 12px;
          margin-top: 16px;
        }

        .file-preview-icon {
          width: 48px;
          height: 48px;
          border-radius: 8px;
          background: #e0f2fe;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #0d6efd;
        }

        .file-preview-info {
          text-align: left;
          flex: 1;
        }

        .file-preview-name {
          font-weight: 500;
          color: #1e293b;
          margin-bottom: 4px;
        }

        .file-preview-size {
          font-size: 12px;
          color: #64748b;
        }

        .upload-progress {
          margin-top: 20px;
        }

        .progress-bar {
          height: 8px;
          background: #e2e8f0;
          border-radius: 4px;
          overflow: hidden;
          margin-top: 8px;
        }

        .progress-fill {
          height: 100%;
          background: linear-gradient(90deg, #0d6efd, #0b5ed7);
          transition: width 0.3s ease;
        }

        /* Image Preview Modal */
        .image-preview-modal {
          background: rgba(0, 0, 0, 0.9);
        }

        .image-preview-content {
          background: transparent;
          border: none;
          padding: 0;
          max-width: 90vw;
          max-height: 90vh;
        }

        .preview-image {
          max-width: 100%;
          max-height: 90vh;
          object-fit: contain;
        }

        .image-actions {
          position: absolute;
          top: 20px;
          right: 20px;
          display: flex;
          gap: 8px;
          z-index: 1;
        }

        /* Responsive */
        @media (max-width: 768px) {
          .documents-container {
            padding: 16px;
          }

          .documents-header {
            flex-direction: column;
            align-items: stretch;
            gap: 12px;
          }

          .header-actions {
            flex-direction: column;
          }

          .search-container {
            width: 100%;
          }

          .documents-table {
            font-size: 13px;
          }

          .documents-table th,
          .documents-table td {
            padding: 12px 8px;
          }

          .action-buttons {
            flex-wrap: wrap;
            justify-content: center;
          }

          .file-meta {
            flex-direction: column;
            gap: 4px;
          }

          .bulk-actions {
            flex-direction: column;
            gap: 12px;
            align-items: stretch;
          }

          .bulk-action-buttons {
            justify-content: center;
            flex-wrap: wrap;
          }
        }
        `}
      </style>

      <Header toggleSidebar={toggleSidebar} />
      <div className="main-layout">
        <Sidebar
          isOpen={isSidebarOpen}
          onClose={() => setIsSidebarOpen(false)}
        />
        <main className="content-area">
          <SubHeader />

          <div className="documents-container">
            <div className="documents-card">
              {/* Header */}
              <div className="documents-header">
                <div className="documents-title">
                  <FileText size={28} color="#0d6efd" />
                  <h2>Documents Management</h2>
                </div>
                <div className="header-actions">

                  <button
                    className="btn hover:text-black hover:bg-blue-300 btn-secondary"
                    onClick={fetchDocuments}
                    disabled={loading}
                  >
                    <RefreshCw
                      size={16}
                      className={loading ? "loading-spinner" : " "}
                    />
                    Refresh
                  </button>

                </div>
              </div>

              {/* Filters Section */}
              <div className="filters-section">
                <div
                  className="filters-toggle"
                  onClick={() => setShowFilters(!showFilters)}
                >
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                    }}
                  >
                    <Filter size={16} />
                    <span style={{ fontWeight: 500, color: "#475569" }}>
                      Filters
                    </span>
                    {(activeFilters.fileType !== "all" ||
                      activeFilters.dateRange !== "all" ||
                      activeFilters.unitNo) && (
                        <span
                          style={{
                            background: "#0d6efd",
                            color: "white",
                            fontSize: "12px",
                            padding: "2px 8px",
                            borderRadius: "12px",
                          }}
                        >
                          Active
                        </span>
                      )}
                  </div>
                  {showFilters ? (
                    <ChevronUp size={18} />
                  ) : (
                    <ChevronDown size={18} />
                  )}
                </div>

                {showFilters && (
                  <div className="filters-content">
                    <div className="filter-group">
                      <label>File Type</label>
                      <select
                        className="filter-select"
                        value={activeFilters.fileType}
                        onChange={(e) =>
                          setActiveFilters((prev) => ({
                            ...prev,
                            fileType: e.target.value,
                          }))
                        }
                      >
                        <option value="all">All Types</option>
                        <option value="image">Images</option>
                        <option value="pdf">PDF Documents</option>
                        <option value="document">Word Documents</option>
                        <option value="spreadsheet">Spreadsheets</option>
                      </select>
                    </div>

                    <div className="filter-group">
                      <label>Unit Number</label>
                      <input
                        type="text"
                        className="filter-input"
                        placeholder="Filter by unit number..."
                        value={activeFilters.unitNo}
                        onChange={(e) =>
                          setActiveFilters((prev) => ({
                            ...prev,
                            unitNo: e.target.value,
                          }))
                        }
                      />
                    </div>

                    <div className="filter-group">
                      <label>Date Range</label>
                      <select
                        className="filter-select"
                        value={activeFilters.dateRange}
                        onChange={(e) =>
                          setActiveFilters((prev) => ({
                            ...prev,
                            dateRange: e.target.value,
                          }))
                        }
                      >
                        <option value="all">All Time</option>
                        <option value="today">Today</option>
                        <option value="week">This Week</option>
                        <option value="month">This Month</option>
                        <option value="year">This Year</option>
                      </select>
                    </div>

                    <div style={{ gridColumn: "1 / -1" }}>
                      <button
                        className="clear-filters-btn"
                        onClick={clearFilters}
                      >
                        Clear All Filters
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* Documents Table */}
              <div className="documents-body">
                {loading ? (
                  <div className="loading-state">
                    <Loader2 size={32} className="loading-spinner" />
                    <h3>Loading documents...</h3>
                  </div>
                ) : error ? (
                  <div className="empty-state">
                    <AlertCircle size={48} color="#ef4444" />
                    <h3>Error Loading Documents</h3>
                    <p>{error}</p>
                    <button
                      className="btn btn-secondary"
                      onClick={fetchDocuments}
                    >
                      Try Again
                    </button>
                  </div>
                ) : filteredDocuments.length === 0 ? (
                  <div className="empty-state">
                    <FileText size={48} color="#cbd5e0" />
                    <h3>No Documents Found</h3>
                    <p>
                      {searchQuery ||
                        Object.values(activeFilters).some(
                          (f) => f !== "all" && f !== "",
                        )
                        ? "Try changing your search or filters"
                        : "Upload your first document to get started"}
                    </p>
                    <button
                      className="btn btn-primary"
                      onClick={() => setIsUploadModalOpen(true)}
                    >
                      <Upload size={16} />
                      Upload Document
                    </button>
                  </div>
                ) : (
                  <>
                    <table className="documents-table">
                      <thead>
                        <tr>
                          <th className="select-cell border">
                            <input
                              type="checkbox"
                              checked={
                                selectedDocuments.length ===
                                filteredDocuments.length
                              }
                              onChange={handleSelectAll}
                            />
                          </th>
                          <th
                            className={`border ${sortConfig.key === "unitNo" ? "sorted" : ""}`}
                            onClick={() => handleSort("unitNo")}
                          >
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "8px",
                              }}
                            >
                              <Home size={14} />
                              Unit No
                              {sortConfig.key === "unitNo" && (
                                <span className="sort-indicator">
                                  {sortConfig.direction === "asc" ? "↑" : "↓"}
                                </span>
                              )}
                            </div>
                          </th>
                          <th
                            className={`border ${sortConfig.key === "fileName" ? "sorted" : ""}`}
                            onClick={() => handleSort("fileName")}
                          >
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "8px",
                              }}
                            >
                              <FileText size={14} />
                              Document Name
                              {sortConfig.key === "fileName" && (
                                <span className="sort-indicator">
                                  {sortConfig.direction === "asc" ? "↑" : "↓"}
                                </span>
                              )}
                            </div>
                          </th>
                          <th className="border">Type</th>
                          <th
                            className={` border ${sortConfig.key === "createDate" ? "sorted" : ""}`}
                            onClick={() => handleSort("createDate")}
                          >
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "8px",
                              }}
                            >
                              <Calendar size={14} />
                              Upload Date
                              {sortConfig.key === "createDate" && (
                                <span className="sort-indicator">
                                  {sortConfig.direction === "asc" ? "↑" : "↓"}
                                </span>
                              )}
                            </div>
                          </th>
                          <th>
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "8px",
                                cursor: "pointer"
                              }}
                              onClick={() => handleSort("subject")} // ✅ sorting trigger
                            >
                              <Calendar size={14} />
                              Subject

                              {sortConfig.key === "subject" && (
                                sortConfig.direction === "ascending" ? (
                                  <span>▲</span>
                                ) : (
                                  <span>▼</span>
                                )
                              )}
                            </div>
                          </th>
                          <th className="border">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredDocuments.map((doc) => (
                          <tr
                            key={doc.documentId}
                            className={
                              selectedDocuments.includes(doc.documentId)
                                ? "selected"
                                : ""
                            }
                          >
                            <td className="select-cell border">
                              <input
                                type="checkbox"
                                checked={selectedDocuments.includes(
                                  doc.documentId,
                                )}
                                onChange={() =>
                                  handleDocumentSelect(doc.documentId)
                                }
                              />
                            </td>
                            <td className="border">
                              <div
                                style={{
                                  padding: "6px 12px",
                                  background: "#f0f9ff",
                                  borderRadius: "6px",
                                  display: "inline-block",
                                  fontWeight: 500,
                                  color: "#0d6efd",
                                }}
                              >
                                {doc.unitNo}
                              </div>
                            </td>
                            <td className="border">
                              <div className="file-cell">
                                <div
                                  className="file-icon"
                                  style={{
                                    background: `${getFileColor(doc.fileType)}20`,
                                    color: getFileColor(doc.fileType),
                                  }}
                                >
                                  {getFileIcon(doc.fileType)}
                                </div>
                                <div className="file-info">
                                  <span
                                    className="file-name"
                                    title={doc.fileName}
                                  >
                                    {doc.fileName}
                                  </span>
                                  <div className="file-meta">
                                    <span>{formatFileSize(doc.fileSize)}</span>
                                    <span>•</span>
                                    <span>
                                      {doc.fileType
                                        .split("/")[1]
                                        ?.toUpperCase() || doc.fileType}
                                    </span>
                                  </div>
                                </div>
                              </div>
                            </td>
                            <td className="border">
                              <span
                                style={{
                                  padding: "4px 8px",
                                  background: getFileColor(doc.fileType) + "20",
                                  color: getFileColor(doc.fileType),
                                  borderRadius: "4px",
                                  fontSize: "12px",
                                  fontWeight: 500,
                                }}
                              >
                                {doc.fileType.split("/")[0]?.toUpperCase() ||
                                  "FILE"}
                              </span>
                            </td>
                            <td className="border">
                              <div
                                style={{
                                  display: "flex",
                                  flexDirection: "column",
                                  gap: "4px",
                                }}
                              >
                                <span style={{ fontWeight: 500 }}>
                                  {formatDateTime(doc.createDate)}
                                </span>
                              </div>
                            </td>
                            <td className="border">
                              {doc.subject}
                            </td>
                            <td className="border">
                              <div className="action-buttons">
                                {doc.fileType.startsWith("image/") ? (
                                  <>
                                    <button
                                      className="border-none p-2 rounded bg-yellow-200 view"
                                      title="Preview"
                                      onClick={() =>
                                        setPreviewImage(
                                          `data:${doc.fileType};base64,${doc.base64}`
                                        )
                                      }
                                    >
                                      <Eye size={16} />
                                    </button>
                                  </>
                                ) : doc.fileType.includes("pdf") ? (
                                  <>
                                    <button
                                      className="border-none p-2 rounded bg-blue-300 view"
                                      title="View PDF"
                                      onClick={() =>
                                        setPreviewPdf(
                                          `data:${doc.fileType};base64,${doc.base64}`
                                        )
                                      }
                                    >
                                      <Eye size={16} />
                                    </button>
                                  </>
                                ) : (
                                  <>
                                    <button
                                      className="border-none p-2 rounded bg-green-300 download"
                                      title="Download"
                                      onClick={() => handleDownload(doc)}
                                    >
                                      <Download size={16} />
                                    </button>
                                  </>
                                )}

                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </>
                )}
              </div>

              {selectedDocuments.length > 0 && (
                <div className="bulk-actions">
                  <div className="selected-count">
                    {selectedDocuments.length} document
                    {selectedDocuments.length !== 1 ? "s" : ""} selected
                  </div>
                  <div className="bulk-action-buttons">
                    <button
                      className="btn btn-danger"
                      onClick={() => {
                        selectedDocuments.forEach(handleDelete);
                        setSelectedDocuments([]);
                      }}
                    >
                      <Trash2 size={16} />
                      Delete Selected
                    </button>
                    <button
                      className="btn btn-secondary"
                      onClick={() => {
                        selectedDocuments.forEach((id) => {
                          const doc = documents.find(
                            (d) => d.documentId === id,
                          );
                          if (doc) handleDownload(doc);
                        });
                      }}
                    >
                      <Download size={16} />
                      Download Selected
                    </button>
                    <button
                      className="btn btn-secondary"
                      onClick={() => setSelectedDocuments([])}
                    >
                      <X size={16} />
                      Clear Selection
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </main>
      </div>

      {/* Upload Modal */}
      {isUploadModalOpen && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3 style={{ color: "#007BFF" }}>Upload Document</h3>
              <button
                className="close-modal"
                onClick={() => setIsUploadModalOpen(false)}
              >
                <X size={20} />
              </button>
            </div>
            <div className="form-group">
              <label>Unit Number</label>
              <input
                type="text"
                className="filter-input"
                placeholder="Enter unit number (e.g., A-101)"
                value={uploadUnitNo}
                onChange={(e) => setUploadUnitNo(e.target.value)}
              />
            </div>
            <div className="form-group">
              <label>Select File</label>
              <div
                className={`file-upload-area ${uploadFile ? "has-file" : ""}`}
                onClick={() => fileInputRef.current?.click()}
              >
                <input
                  type="file"
                  ref={fileInputRef}
                  style={{ display: "none" }}
                  onChange={(e) => setUploadFile(e.target.files[0])}
                />
                {uploadFile ? (
                  <div className="file-preview">
                    <div className="file-preview-icon">
                      <File size={24} />
                    </div>
                    <div className="file-preview-info">
                      <div className="file-preview-name">{uploadFile.name}</div>
                      <div className="file-preview-size">
                        {formatFileSize(uploadFile.size)} • {uploadFile.type}
                      </div>
                    </div>
                  </div>
                ) : (
                  <>
                    <Upload size={48} color="#94a3b8" />
                    <p style={{ marginTop: "12px", color: "#64748b" }}>
                      Click to browse or drag and drop files
                    </p>
                    <p
                      style={{
                        fontSize: "12px",
                        color: "#94a3b8",
                        marginTop: "4px",
                      }}
                    >
                      Max file size: 10MB
                    </p>
                  </>
                )}
              </div>
            </div>
            <div style={{ display: "flex", gap: "12px", marginTop: "24px" }}>
              <button
                className="btn btn-secondary"
                onClick={() => setIsUploadModalOpen(false)}
                disabled={isUploading}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={handleUpload}
                disabled={isUploading}
              >
                {isUploading ? (
                  <>
                    <Loader2 size={16} className="loading-spinner" />
                    Uploading...
                  </>
                ) : (
                  <>
                    <Upload size={16} style={{ color: "#007BFF" }} />
                    Upload Document
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Image Preview Modal */}
      {previewImage && (
        <div
          className="modal-overlay image-preview-modal"
          onClick={() => setPreviewImage(null)}
        >
          <div
            className="modal-content image-preview-content"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="image-actions">
              <button
                className="border-none rounded w-20 h-10 bg-green-400"
                onClick={() => {
                  const link = document.createElement("a");
                  link.href = previewImage;
                  link.download = "document-preview.png";
                  document.body.appendChild(link);
                  link.click();
                  document.body.removeChild(link);
                }}
              >
                <Download size={16} />
              </button>
              <button
                className="border-none rounded w-20 h-10 bg-red-400 "
                onClick={() => setPreviewImage(null)}
              >
                <X size={16} />
              </button>
            </div>
            <img src={previewImage} alt="Preview" className="preview-image" />
          </div>
        </div>
      )}
      {previewPdf && (
        <div
          className="modal-overlay image-preview-modal"
          onClick={() => setPreviewPdf(null)}
        >
          <div
            className="modal-content image-preview-content"
            onClick={(e) => e.stopPropagation()}
            style={{ width: "90%", height: "90%" }}
          >
            <div className="image-actions">
              <button
                className="border-none rounded w-20 h-10 bg-green-400"
                onClick={() => {
                  const link = document.createElement("a");
                  link.href = previewPdf;
                  link.download = "document.pdf";
                  document.body.appendChild(link);
                  link.click();
                  document.body.removeChild(link);
                }}
              >
                <Download size={16} />
              </button>

              <button
                className="border-none rounded w-20 h-10 bg-red-400"
                onClick={() => setPreviewPdf(null)}
              >
                <X size={16} />
              </button>
            </div>

            <iframe
              src={previewPdf}
              title="PDF Preview"
              style={{ width: "100%", height: "100%", border: "none" }}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default DocumentsList;
